import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DATE_FORMAT } from "@redotech/redo-web/time";
import { memo, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import * as styles from "./order-summary-card.module.css";
import {
  formatCustomerName,
  orderCurrency,
  OrderDoc,
  parseAmount,
  totalItemCount,
} from "./parse-order-data";

/**
 * Two styled cards for the two known shapes of `get_order` output: success
 * and error. Exported individually so the tool-renderers registry can
 * dispatch the right card based on the parsed shape — the registry parses,
 * this file just renders. Mirrors the return-summary-card layout so order
 * and return results look consistent in Ask Redo.
 */

export const OrderErrorCard = memo(function OrderErrorCard({
  error,
}: {
  error: string;
}) {
  return (
    <div className={styles.errorCard}>
      <Text fontSize="sm" textColor="primary">
        {error}
      </Text>
    </div>
  );
});

export const OrderDocCard = memo(function OrderDocCard({
  doc,
}: {
  doc: OrderDoc;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  // Order detail nests under the outbound-labels orders list:
  //   /stores/<id>/shipping/outbound-labels/orders/<orderId>
  const orderUrl = routes.getOrdersOutboundLabelsSubRoute(`orders/${doc.id}`);

  const customerName = formatCustomerName(doc.customer);
  const customerEmail = doc.customer?.emailAddress?.trim() || null;
  const itemCount = totalItemCount(doc.items);
  const moneyFormatter = getCurrencyFormatter(orderCurrency(doc));
  const total = parseAmount(doc.total);
  const placedAt = parseIsoDate(doc.placedAt);

  const orderLabel = doc.name?.trim()
    ? doc.name.trim().startsWith("#")
      ? doc.name.trim()
      : `#${doc.name.trim()}`
    : null;
  const headline = orderLabel
    ? `Order ${orderLabel}`
    : `Order ${shortId(doc.id)}`;

  return (
    <div className={styles.card}>
      <Flex dir="column" gap="xs">
        <Flex align="center" gap="sm" wrap="wrap">
          <Text fontSize="md" fontWeight="semibold">
            {headline}
          </Text>
        </Flex>
        {(customerName || customerEmail) && (
          <Text fontSize="xs" textColor="secondary">
            {[customerName, customerEmail].filter(Boolean).join(" · ")}
          </Text>
        )}
      </Flex>

      <Flex dir="column">
        <Row
          isLast={!placedAt && total === null}
          label="Items"
          value={<RedoBadge color="gray" size="xs" text={String(itemCount)} />}
        />
        {placedAt && (
          <Row
            isLast={total === null}
            label="Ordered"
            value={
              <Text fontSize="xs" textColor="primary">
                {DATE_FORMAT.format(placedAt)}
              </Text>
            }
          />
        )}
        {total !== null && (
          <Row
            isLast
            label="Total"
            value={
              <Text fontSize="xs" fontWeight="semibold">
                {moneyFormatter.format(total)}
              </Text>
            }
          />
        )}
      </Flex>

      <Flex dir="column" gap="sm">
        <RedoButton
          className={styles.actionButton}
          hierarchy="secondary"
          onClick={() => navigate(orderUrl)}
          size="sm"
          text="View order"
        />
      </Flex>
    </div>
  );
});

const Row = memo(function Row({
  label,
  value,
  isLast,
}: {
  label: string;
  value: ReactNode;
  isLast?: boolean;
}) {
  return (
    <>
      <Flex
        align="center"
        className={styles.row}
        justify="space-between"
        py="sm"
        w="full"
      >
        <Text fontSize="xs" textColor="primary">
          {label}
        </Text>
        {value}
      </Flex>
      {!isLast && <Divider />}
    </>
  );
});

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
